// Global Drifter Program near-surface currents — the observed counterpart to the
// modelled Surface-current (SMOC) track, and the console's fifth use of the rule
// that OBSERVED sits beside MODELLED, never blended, always with provenance.
//
// Les Bender wants this because the Loop Current and its eddies run 3-4 kn in the
// Gulf — ±30% of a 10-12 kn survey vessel's transit speed — and the console's
// Surface-current chart, fed by Météo-France SMOC, was the one track with nothing
// observed standing beside it. A drifter drogued at 15 m is a direct measurement of
// exactly the water that pushes the hull.
//
// TWO UPSTREAM FACTS shaped this module; both were verified by curl on 2026-07-20
// and both differ from what the task brief assumed, so they are written out in full:
//
//   1. THERE IS NO LIVE ve/vn PRODUCT. AOML's `drifter_hourly_qc` — the dataset the
//      brief pointed at, the one carrying gap-filled eastward/northward velocities —
//      is a quality-controlled ARCHIVE that ends 2022-10-31 (its 6-hour sibling
//      reaches only mid-2025). Neither can say what is drifting past a site THIS
//      week. The only near-real-time GDP feed is OSMC_RealTime (AOML's flattening of
//      the GTS drifter reports), and it carries hourly POSITIONS but leaves its
//      uo/vo current columns null for every drifter. So this module DERIVES the
//      current from the position track by centred finite differences — which is what
//      ve/vn physically ARE before the archive kriges them. The observation is the
//      position (a real GTS fix); the velocity is computed from it, and every label
//      that shows a current says "GDP drifter … observed" so the provenance is on
//      screen. A parked or beached drifter correctly derives ~0 kn.
//
//   2. AOML's ERDDAP SENDS NO CORS HEADERS (no Access-Control-Allow-Origin even with
//      an Origin present; the OPTIONS preflight is a bare 403). The Argo path —
//      client-direct to Ifremer, which answers `*` — is therefore closed here, so
//      this fetches through the Go relay at /api/drifters (see server/drifters.go),
//      the same reason the NDBC and AODN layers relay.
//
// This module does NO rendering and touches NO DOM: data in, typed drifters out. The
// map overlay (overlays.ts) draws the glyph, vector and tail; main.ts buckets the
// derived speed onto the Surface-current chart and reports health. The three-state
// fetch contract, request-id guard and containment cache all live at the call site,
// exactly as they do for Argo.

const MPS_TO_KN = 1.94384;
const MS_PER_HOUR = 3_600_000;
/** Metres per degree of latitude (mean); longitude scales this by cos(lat). Good to
 *  a few parts in 1000 over a drifter's hourly hop — far finer than the position
 *  quantisation this differencing has to see through. */
const M_PER_DEG_LAT = 111_195;

const EARTH_RADIUS_NM = 3440.065;
const toRad = (d: number): number => (d * Math.PI) / 180;

/** Great-circle distance in nm — the same haversine as argo.ts and primer.ts. */
function haversineNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_NM * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** One time-stamped fix on a drifter's track, with the current derived AT that fix.
 *  ve/vn (m/s, east/north) are null when the fix has no usable neighbour to difference
 *  against (a lone fix, or a zero time gap). */
export interface DrifterFix {
  timeUtcMs: number;
  lat: number;
  lon: number;
  /** Derived eastward / northward velocity, m/s. Null where undifferenceable. */
  ve: number | null;
  vn: number | null;
  /** Distance from the clicked site to this fix, nm. Lets the chart admit only the
   *  fixes taken while the drifter was actually near the site. */
  distanceNm: number;
}

/** One drifter: its identity, its latest state, and its ~5-day tail. */
export interface Drifter {
  /** GDP buoy id, formatted as a plain integer string (OSMC reports it as a float,
   *  e.g. 6256567645.0 — rendered here as "6256567645"). */
  id: string;
  /** Latest fix (the glyph's position and the popup's "age of fix"). */
  timeUtcMs: number;
  lat: number;
  lon: number;
  /** Closest this drifter's whole tail came to the site, nm — the number the chart
   *  uses to decide whether the drifter passed within range. */
  minDistanceNm: number;
  /**
   * Smoothed recent current for the map vector: net displacement over the most
   * recent window (≤ LATEST_WINDOW_H). Null when the tail is too short or too brief
   * to state a velocity, in which case the map draws the glyph but no vector.
   */
  speedKn: number | null;
  /** Set (direction the current flows TOWARD), degrees true. Null with speedKn. */
  headingDeg: number | null;
  /** Latest reported sea-surface temperature, °C, or null (many fixes carry none). */
  sstC: number | null;
  /** The tail, oldest → newest, as [lon, lat] for a GeoJSON LineString. */
  tail: [number, number][];
  /** Per-fix kinematics (oldest → newest) for bucketing observed speed onto a chart. */
  fixes: DrifterFix[];
}

/**
 * The outcome of asking for drifters near a site, with the two absences kept apart —
 * the same discipline as ArgoFetch / FieldFetch, for the same reason: a dropped
 * request must not look like an empty ocean.
 *
 *   { ok: true, drifters: [...] } — the source answered; zero-length means it
 *                                   answered "no drifter in this box/window", which
 *                                   is a true fact about the ocean and entirely
 *                                   normal (drifters are sparse), NOT a failure.
 *   { ok: false }                 — we failed to ASK (network, 5xx, unparseable).
 *                                   The caller keeps whatever overlay it holds.
 */
export type DrifterFetch = { ok: true; drifters: Drifter[] } | { ok: false };

/** Half-width of the search box, degrees. Mirrors the server's drifterBoxDeg; kept
 *  here only for the containment cache's reasoning, since the server owns the query. */
const BOX_DEG = 6.0;
/** Fewest position fixes that make a track worth differencing into a current. One
 *  fix is still a valid observed POSITION (glyph + popup), but it can draw no tail
 *  and no vector. */
const MIN_TAIL_FIXES = 2;
/** Window for the map's smoothed "recent" velocity vector, ms. Net displacement over
 *  the last up-to-12 h beats the per-hour position quantisation without averaging
 *  away a real turn through an eddy. */
const LATEST_WINDOW_H = 12;
/** Shortest baseline (ms) over which the smoothed vector is trusted. Under an hour of
 *  motion, position rounding dominates the displacement, so no vector is claimed. */
const MIN_LATEST_MS = 3_600_000;

interface RawTable {
  columnNames: string[];
  rows: unknown[][];
}

function asTable(json: unknown): RawTable | null {
  if (typeof json !== "object" || json === null) return null;
  const t = (json as { table?: unknown }).table;
  if (typeof t !== "object" || t === null) return null;
  const names = (t as { columnNames?: unknown }).columnNames;
  const rows = (t as { rows?: unknown }).rows;
  if (!Array.isArray(names) || !Array.isArray(rows)) return null;
  return { columnNames: names.map(String), rows: rows as unknown[][] };
}

const numOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/** Format the GDP id: OSMC serves it as a float (6256567645.0). Round to an integer
 *  and print without a fraction or exponent, so the popup shows a clean buoy number. */
function formatId(v: unknown): string {
  const n = numOrNull(v);
  if (n === null) return String(v ?? "");
  return Math.round(n).toLocaleString("en-US", { useGrouping: false });
}

/**
 * Centred finite-difference velocity along a drifter's own track.
 *
 * For an interior fix we difference the fix before against the fix after (a centred
 * estimate, least biased); the endpoints fall back to the one adjacent fix they have.
 * A non-positive time gap yields a null velocity for that fix rather than a divide by
 * zero. This is the raw current the GDP archive would later gap-fill into ve/vn — the
 * honest, live version of the same quantity, and it is the ONLY current available
 * because OSMC's uo/vo columns are null (see the module header).
 */
function differentiate(fixes: DrifterFix[]): void {
  const n = fixes.length;
  for (let i = 0; i < n; i++) {
    const a = fixes[Math.max(0, i - 1)];
    const b = fixes[Math.min(n - 1, i + 1)];
    const dtSec = (b.timeUtcMs - a.timeUtcMs) / 1000;
    if (dtSec <= 0) {
      fixes[i].ve = null;
      fixes[i].vn = null;
      continue;
    }
    const midLat = (a.lat + b.lat) / 2;
    const dNorth = (b.lat - a.lat) * M_PER_DEG_LAT;
    const dEast = (b.lon - a.lon) * M_PER_DEG_LAT * Math.cos(toRad(midLat));
    fixes[i].ve = dEast / dtSec;
    fixes[i].vn = dNorth / dtSec;
  }
}

/** Bearing (degrees true, 0-360) of an east/north velocity — the set, i.e. the
 *  direction the current flows TOWARD. */
function bearingToward(ve: number, vn: number): number {
  const deg = (Math.atan2(ve, vn) * 180) / Math.PI;
  return deg < 0 ? deg + 360 : deg;
}

/**
 * Build the smoothed recent velocity for the map vector from net displacement over
 * the last LATEST_WINDOW_H of the tail. Returns null when the tail is too short or the
 * usable window too brief to state a current honestly.
 */
function recentVelocity(fixes: DrifterFix[]): { speedKn: number; headingDeg: number } | null {
  if (fixes.length < MIN_TAIL_FIXES) return null;
  const last = fixes[fixes.length - 1];
  const cutoff = last.timeUtcMs - LATEST_WINDOW_H * MS_PER_HOUR;
  // Earliest fix still inside the window; fixes are oldest → newest.
  let first = fixes[fixes.length - 1];
  for (let i = fixes.length - 1; i >= 0; i--) {
    if (fixes[i].timeUtcMs < cutoff) break;
    first = fixes[i];
  }
  const dtSec = (last.timeUtcMs - first.timeUtcMs) / 1000;
  if (dtSec * 1000 < MIN_LATEST_MS) return null;
  const midLat = (first.lat + last.lat) / 2;
  const dNorth = (last.lat - first.lat) * M_PER_DEG_LAT;
  const dEast = (last.lon - first.lon) * M_PER_DEG_LAT * Math.cos(toRad(midLat));
  const ve = dEast / dtSec;
  const vn = dNorth / dtSec;
  return { speedKn: Math.hypot(ve, vn) * MPS_TO_KN, headingDeg: bearingToward(ve, vn) };
}

/**
 * Fetch drifters near a site through the Go relay and turn the flat OSMC table into
 * typed, per-drifter tracks with a derived current at each fix.
 *
 * The relay owns the ERDDAP query (dataset, columns, "DRIFTING BUOYS" filter, ±6° box,
 * 5-day window); this only passes the site and parses. A 404 from the relay is the
 * "no rows matched" answer forwarded from ERDDAP — an empty ocean, returned here as
 * ok+[] so the caller can tell it from a transport failure.
 */
export async function fetchDrifters(lat: number, lon: number): Promise<DrifterFetch> {
  let res: Response;
  try {
    // The OSMC scan can take 15-25 s cold, and the relay bounds it at 35 s; give the
    // client a little more so a slow-but-successful answer is not aborted into a
    // false transport failure.
    res = await fetch(
      `/api/drifters?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`,
      { signal: AbortSignal.timeout(40_000) },
    );
  } catch {
    return { ok: false }; // network dropped, aborted or timed out — ask again later
  }
  // 404 is the relay forwarding ERDDAP's "no matching results": a real, authoritative
  // "no drifter in this box/window", not a fault.
  if (res.status === 404) return { ok: true, drifters: [] };
  if (!res.ok) return { ok: false };

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    return { ok: false };
  }
  const table = asTable(json);
  if (table === null) return { ok: false }; // shape we don't recognise — a fault, not empty sea

  const idx = (name: string): number => table.columnNames.indexOf(name);
  const iId = idx("platform_id");
  const iTime = idx("time");
  const iLat = idx("latitude");
  const iLon = idx("longitude");
  const iSst = idx("sst");
  if ([iId, iTime, iLat, iLon].some((i) => i < 0)) return { ok: false }; // a needed column is gone

  // Group the flat table into per-drifter fix lists. OSMC does not guarantee an
  // order, so the tail is sorted by time after grouping rather than trusting the rows.
  interface Group {
    id: string;
    fixes: DrifterFix[];
    sst: { t: number; v: number } | null; // newest non-null sst seen
  }
  const groups = new Map<string, Group>();
  for (const row of table.rows) {
    const plat = numOrNull(row[iId]);
    const timeMs = Date.parse(String(row[iTime] ?? ""));
    const rlat = numOrNull(row[iLat]);
    const rlon = numOrNull(row[iLon]);
    if (plat === null || rlat === null || rlon === null || !Number.isFinite(timeMs)) continue;
    const key = formatId(plat);
    let g = groups.get(key);
    if (g === undefined) {
      g = { id: key, fixes: [], sst: null };
      groups.set(key, g);
    }
    g.fixes.push({
      timeUtcMs: timeMs,
      lat: rlat,
      lon: rlon,
      ve: null,
      vn: null,
      distanceNm: haversineNm(lat, lon, rlat, rlon),
    });
    const sst = iSst >= 0 ? numOrNull(row[iSst]) : null;
    if (sst !== null && (g.sst === null || timeMs > g.sst.t)) g.sst = { t: timeMs, v: sst };
  }

  const drifters: Drifter[] = [];
  for (const g of groups.values()) {
    g.fixes.sort((a, b) => a.timeUtcMs - b.timeUtcMs);
    // Collapse exact-duplicate timestamps (OSMC occasionally repeats a fix): a zero
    // gap would only produce null velocities and clutter the tail.
    const fixes: DrifterFix[] = [];
    for (const f of g.fixes) {
      const prev = fixes[fixes.length - 1];
      if (prev !== undefined && prev.timeUtcMs === f.timeUtcMs) continue;
      fixes.push(f);
    }
    if (fixes.length === 0) continue;
    differentiate(fixes);
    const last = fixes[fixes.length - 1];
    const recent = recentVelocity(fixes);
    drifters.push({
      id: g.id,
      timeUtcMs: last.timeUtcMs,
      lat: last.lat,
      lon: last.lon,
      minDistanceNm: fixes.reduce((m, f) => Math.min(m, f.distanceNm), Infinity),
      speedKn: recent?.speedKn ?? null,
      headingDeg: recent?.headingDeg ?? null,
      sstC: g.sst?.v ?? null,
      tail: fixes.length >= MIN_TAIL_FIXES ? fixes.map((f) => [f.lon, f.lat]) : [],
      fixes,
    });
  }
  return { ok: true, drifters };
}

/** Age of a drifter's latest fix in hours (for the popup and the freshness stamp). */
export function drifterAgeHours(d: Drifter, nowMs: number = Date.now()): number {
  return Math.max(0, (nowMs - d.timeUtcMs) / MS_PER_HOUR);
}

/** Newest fix time across a set of drifters — the Systems row's data stamp. Null for
 *  an empty set (there is nothing to be stale). */
export function driftersNewestMs(drifters: Drifter[]): number | null {
  let newest: number | null = null;
  for (const d of drifters) if (newest === null || d.timeUtcMs > newest) newest = d.timeUtcMs;
  return newest;
}

// --- containment cache (mirrors argoStillRepresents) --------------------------
//
// Drifters are fetched site-keyed, not on map moveend: a click near the last fetch is
// still inside the same ±6° box, so the held set still describes this neighbourhood
// and the slow OSMC scan can be skipped. The reuse radius is deliberately well inside
// the box half-width so a reused set never sits near its own edge.

/** How far a new site may drift from the fetch centre before a refetch is worth it. */
const REUSE_NM = 120;

/** A held drifter set, tagged with the site it was fetched for. */
export interface HeldDrifters {
  lat: number;
  lon: number;
  drifters: Drifter[];
}

/** True when a set fetched at (held.lat, held.lon) still represents (lat, lon): the
 *  new point is near the old fetch centre, so the same ±6° box still contains it. */
export function driftersStillRepresent(held: HeldDrifters, lat: number, lon: number): boolean {
  return haversineNm(lat, lon, held.lat, held.lon) <= REUSE_NM && REUSE_NM < BOX_DEG * 60;
}

// --- chart selection & bucketing (Task 2, the Surface-current track) ----------

/** Farthest a drifter's closest approach may be from the site for its current to be
 *  admitted onto the chart as an observation of THIS point's water. */
export const DRIFTER_CHART_MAX_NM = 30;

/**
 * The drifter whose track passed closest to the site, if any came within `maxNm`.
 * Absence is normal — most sites have no drifter within 30 nm — and returns null.
 */
export function nearestDrifter(
  drifters: Drifter[],
  maxNm: number = DRIFTER_CHART_MAX_NM,
): Drifter | null {
  let best: Drifter | null = null;
  for (const d of drifters) {
    if (d.minDistanceNm <= maxNm && (best === null || d.minDistanceNm < best.minDistanceNm)) {
      best = d;
    }
  }
  return best;
}

/**
 * Average a drifter's DERIVED speed (kn) into the forecast's hourly buckets, admitting
 * only the fixes taken while the drifter was within `maxNm` of the site. Time handling
 * is identical to ndbc.ts's bucketObs and carries the same site-local discipline: the
 * `times` are the chart's LOCAL hour labels ("2026-07-11T14:00") and a label's UTC
 * hour start is `Date.parse(label + ":00Z") - utcOffsetSeconds*1000`. Getting that
 * offset wrong once displaced a whole trace by five hours, so it is passed explicitly
 * and applied exactly as the wind/wave obs are. Hours with no qualifying fix are null.
 */
export function bucketDrifterSpeed(
  d: Drifter,
  times: string[],
  utcOffsetSeconds: number,
  maxNm: number = DRIFTER_CHART_MAX_NM,
): (number | null)[] {
  const out: (number | null)[] = new Array<number | null>(times.length).fill(null);
  if (times.length === 0) return out;
  const t0 = Date.parse(times[0] + ":00Z") - utcOffsetSeconds * 1000;
  if (Number.isNaN(t0)) return out;

  const sum = new Array<number>(times.length).fill(0);
  const cnt = new Array<number>(times.length).fill(0);
  for (const f of d.fixes) {
    if (f.ve === null || f.vn === null) continue; // undifferenceable fix — no speed
    if (f.distanceNm > maxNm) continue; // out of range at this fix — not this point's water
    const speedKn = Math.hypot(f.ve, f.vn) * MPS_TO_KN;
    const i = Math.round((f.timeUtcMs - t0) / MS_PER_HOUR);
    if (i >= 0 && i < times.length) {
      sum[i] += speedKn;
      cnt[i]++;
    }
  }
  for (let i = 0; i < times.length; i++) if (cnt[i] > 0) out[i] = sum[i] / cnt[i];
  return out;
}
